import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  CircularProgress, 
  Alert,
  Paper
} from '@mui/material';
import DataTable from '../components/DataTable';
import { 
  getSfdcContacts, 
  getSfdcAccounts, 
  getSfdcOpportunities 
} from '../services/api';
import { 
  SfdcContact, 
  SfdcAccount, 
  SfdcOpportunity 
} from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sfdc-tabpanel-${index}`}
      aria-labelledby={`sfdc-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const SfdcData: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [contacts, setContacts] = useState<SfdcContact[]>([]);
  const [accounts, setAccounts] = useState<SfdcAccount[]>([]);
  const [opportunities, setOpportunities] = useState<SfdcOpportunity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch data based on the active tab
        if (tabValue === 0 && contacts.length === 0) {
          const contactsData = await getSfdcContacts();
          setContacts(contactsData);
        } else if (tabValue === 1 && accounts.length === 0) {
          const accountsData = await getSfdcAccounts();
          setAccounts(accountsData);
        } else if (tabValue === 2 && opportunities.length === 0) {
          const opportunitiesData = await getSfdcOpportunities();
          setOpportunities(opportunitiesData);
        }
      } catch (err) {
        setError('Failed to fetch data. Please try again later.');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tabValue, contacts.length, accounts.length, opportunities.length]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Define columns for each table
  const contactColumns = [
    { id: 'ContactID', label: 'ID', width: '100px' },
    { id: 'FirstName', label: 'First Name' },
    { id: 'LastName', label: 'Last Name' },
    { id: 'Email', label: 'Email' },
    { id: 'Phone', label: 'Phone' },
    { id: 'Title', label: 'Title' },
    { id: 'AccountID', label: 'Account ID' },
    { id: 'CreatedDate', label: 'Created Date' }
  ];

  const accountColumns = [
    { id: 'AccountID', label: 'ID', width: '100px' },
    { id: 'AccountName', label: 'Account Name' },
    { id: 'Industry', label: 'Industry' },
    { id: 'AnnualRevenue', label: 'Annual Revenue', 
      format: (value: number) => value ? `$${value.toLocaleString()}` : '-' },
    { id: 'Region', label: 'Region' }
  ];

  const opportunityColumns = [
    { id: 'OpportunityID', label: 'ID', width: '100px' },
    { id: 'Name', label: 'Name' },
    { id: 'AccountID', label: 'Account ID' },
    { id: 'ContactID', label: 'Contact ID' },
    { id: 'Stage', label: 'Stage' },
    { id: 'Amount', label: 'Amount', 
      format: (value: number) => value ? `$${value.toLocaleString()}` : '-' },
    { id: 'CloseDate', label: 'Close Date' }
  ];

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Salesforce Data
      </Typography>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Contacts" />
          <Tab label="Accounts" />
          <Tab label="Opportunities" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        {loading && contacts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable 
            title="SFDC Contacts" 
            data={contacts} 
            columns={contactColumns} 
          />
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {loading && accounts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable 
            title="SFDC Accounts" 
            data={accounts} 
            columns={accountColumns} 
          />
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {loading && opportunities.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable 
            title="SFDC Opportunities" 
            data={opportunities} 
            columns={opportunityColumns} 
          />
        )}
      </TabPanel>
    </div>
  );
};

export default SfdcData;